import {EventStatistics, EventTimelineUserEntry, EventTopConsumer, EventUserStatistics} from "../../drinkService";

export const normalizeNumber = (value: any): number => {
    if (typeof value === "number") {
        return value;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeString = (value: any, fallback = ""): string => {
    if (typeof value === "string") {
        return value;
    }
    if (value === null || value === undefined) {
        return fallback;
    }
    return String(value);
};

export const normalizeDate = (value: any): Date | null => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};
const toArray = <T>(value: any): T[] => {
    if (Array.isArray(value)) {
        return value as T[];
    }
    if (value === null || value === undefined) {
        return [];
    }
    return [value as T];
};

export const normalizeEventStatisticsResponse = (raw: any): EventStatistics => {
    const users = toArray(raw.users ?? raw.Users).map(normalizeUserStatistics);

    const drinks = toArray(raw.drinks ?? raw.Drinks).map((item: any) => ({
        drinkId: normalizeNumber(item.drinkId ?? item.DrinkId),
        drinkName: normalizeString(item.drinkName ?? item.DrinkName, "Unknown Drink"),
        activityCount: normalizeNumber(item.activityCount ?? item.ActivityCount),
        totalVolumeMl: normalizeNumber(item.totalVolumeMl ?? item.TotalVolumeMl),
        totalAlcoholGrams: normalizeNumber(item.totalAlcoholGrams ?? item.TotalAlcoholGrams),
        topConsumer: normalizeTopConsumer(item.topConsumer ?? item.TopConsumer),
        participants: normalizeParticipants(item.participants ?? item.Participants),
    }));

    const categories = toArray(raw.categories ?? raw.Categories).map((item: any) => ({
        category: normalizeString(item.category ?? item.Category, "Other"),
        activityCount: normalizeNumber(item.activityCount ?? item.ActivityCount),
        totalVolumeMl: normalizeNumber(item.totalVolumeMl ?? item.TotalVolumeMl),
        totalAlcoholGrams: normalizeNumber(item.totalAlcoholGrams ?? item.TotalAlcoholGrams),
        topConsumer: normalizeTopConsumer(item.topConsumer ?? item.TopConsumer),
        participants: normalizeParticipants(item.participants ?? item.Participants),
    }));

    const timeline = toArray(raw.timeline ?? raw.Timeline).map((bucket: any) => ({
        bucketStart: normalizeDate(bucket.bucketStart ?? bucket.BucketStart) ?? new Date(),
        users: toArray(bucket.users ?? bucket.Users).map(normalizeTimelineUserEntry),
    }));

    return {
        eventId: normalizeNumber(raw.eventId ?? raw.EventId),
        eventName: normalizeString(raw.eventName ?? raw.EventName, ""),
        start: normalizeDate(raw.start ?? raw.Start),
        end: normalizeDate(raw.end ?? raw.End),
        participantCount: normalizeNumber(raw.participantCount ?? raw.ParticipantCount),
        users,
        drinks,
        categories,
        timeline,
    };
};


const normalizeTopConsumer = (raw: any): EventTopConsumer | null => {
    if (!raw) {
        return null;
    }
    return {
        userId: normalizeNumber(raw.userId ?? raw.UserId),
        username: normalizeString(raw.username ?? raw.Username, "Unknown"),
        activityCount: normalizeNumber(raw.activityCount ?? raw.ActivityCount),
        totalVolumeMl: normalizeNumber(raw.totalVolumeMl ?? raw.TotalVolumeMl),
        totalAlcoholGrams: normalizeNumber(raw.totalAlcoholGrams ?? raw.TotalAlcoholGrams),
    };
};
export type EventCategoryBreakdown = {
    category: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
};


export type EventDrinkUserBreakdown = {
    drinkId: number;
    drinkName: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
};


const normalizeCategoryBreakdown = (raw: any): EventCategoryBreakdown => ({
    category: normalizeString(raw.category ?? raw.Category, "Other"),
    activityCount: normalizeNumber(raw.activityCount ?? raw.ActivityCount),
    totalVolumeMl: normalizeNumber(raw.totalVolumeMl ?? raw.TotalVolumeMl),
    totalAlcoholGrams: normalizeNumber(raw.totalAlcoholGrams ?? raw.TotalAlcoholGrams),
});

const normalizeDrinkBreakdown = (raw: any): EventDrinkUserBreakdown => ({
    drinkId: normalizeNumber(raw.drinkId ?? raw.DrinkId),
    drinkName: normalizeString(raw.drinkName ?? raw.DrinkName, "Unknown Drink"),
    activityCount: normalizeNumber(raw.activityCount ?? raw.ActivityCount),
    totalVolumeMl: normalizeNumber(raw.totalVolumeMl ?? raw.TotalVolumeMl),
    totalAlcoholGrams: normalizeNumber(raw.totalAlcoholGrams ?? raw.TotalAlcoholGrams),
});

const normalizeParticipants = (raw: any): EventTopConsumer[] =>
    toArray(raw)
        .map((entry) => normalizeTopConsumer(entry))
        .filter((entry): entry is EventTopConsumer => entry !== null);

const normalizeTimelineUserEntry = (raw: any): EventTimelineUserEntry => ({
    userId: normalizeNumber(raw.userId ?? raw.UserId),
    username: normalizeString(raw.username ?? raw.Username, "Unknown"),
    activityCount: normalizeNumber(raw.activityCount ?? raw.ActivityCount),
    totalVolumeMl: normalizeNumber(raw.totalVolumeMl ?? raw.TotalVolumeMl),
    totalAlcoholGrams: normalizeNumber(raw.totalAlcoholGrams ?? raw.TotalAlcoholGrams),
});

const normalizeUserStatistics = (raw: any): EventUserStatistics => ({
    userId: normalizeNumber(raw.userId ?? raw.UserId),
    username: normalizeString(raw.username ?? raw.Username, "Unknown"),
    activityCount: normalizeNumber(raw.activityCount ?? raw.ActivityCount),
    totalVolumeMl: normalizeNumber(raw.totalVolumeMl ?? raw.TotalVolumeMl),
    totalAlcoholGrams: normalizeNumber(raw.totalAlcoholGrams ?? raw.TotalAlcoholGrams),
    categories: toArray(raw.categories ?? raw.Categories).map(normalizeCategoryBreakdown),
    topDrinks: toArray(raw.topDrinks ?? raw.TopDrinks).map(normalizeDrinkBreakdown),
    allDrinks: toArray(raw.allDrinks ?? raw.AllDrinks).map(normalizeDrinkBreakdown),
});